import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAvailabilities } from '@/hooks/useAvailabilities';

interface CalendarProps {
  currentMonth: Date;
  selectedDate: string;
  onDateSelect: (date: string) => void;
  onMonthChange: (date: Date) => void;
}

const Calendar: React.FC<CalendarProps> = ({
  currentMonth,
  selectedDate,
  onDateSelect,
  onMonthChange,
}) => {
  const { availabilities, isLoading } = useAvailabilities();

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    // Convert Sunday (0) to 6, and shift other days by -1 to make Monday (1) the first day
    return firstDay === 0 ? 6 : firstDay - 1;
  };

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  };


  const isDateAvailable = (date: Date) => {
    const today = new Date();
    return date >= today && availabilities.some(doc => doc.id === formatDate(date));
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-12 w-12" />);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isAvailable = isDateAvailable(date);
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const formattedDate = formatDate(date);
      const isSelected = selectedDate === formattedDate;

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => isAvailable && onDateSelect(formattedDate)}
          className={`h-10 w-10 rounded-full flex items-center justify-center transition-all ${
            isSelected
              ? 'bg-orcsys-500 text-white shadow-lg scale-110'
              : isAvailable
                ? 'hover:bg-orcsys-50 hover:scale-105'
                : 'opacity-30 cursor-not-allowed bg-gray-100'
          }`}
          disabled={!isAvailable}
        >
          <span className={`text-sm font-medium ${isWeekend ? 'text-gray-400' : ''}`}>{day}</span>
        </button>
      );
    }

    return days;
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold">Sélectionnez une date</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onMonthChange(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="font-medium">
            {currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
          </span>
          <button
            type="button"
            onClick={() => onMonthChange(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
          <div key={day} className="h-12 w-12 flex items-center justify-center text-sm font-medium text-gray-500">
            {day}
          </div>
        ))}
        {isLoading ? (
          <div className="col-span-7 flex justify-center items-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orcsys-500"></div>
          </div>
        ) : (
          renderCalendar()
        )}
      </div>
    </div>
  );
};

export default Calendar; 